// Recipe-quantity scaler. Mirrors src/recipe_quantity.mjs on the server.
//
// scaleQuantity("1 1/2 cups", 2) → "3 cups"
// scaleQuantity("1/4 tsp", 3)    → "3/4 tsp"
// scaleQuantity("a pinch", 2)    → "a pinch"
// scaleQuantity("3 large eggs", 2) → "6 large eggs"
//
// Strings that don't start with a numeric run pass through unchanged
// ("a pinch", "to taste") — nothing to scale.

const NON_NUMERIC_PREFIX = /^(a |an |to taste|as needed|optional|some\b|pinch\b|dash\b|handful)/i;
const LEADING_NUMBER = /^(\d+\s+\d+\/\d+|\d+\/\d+|\d+(?:\.\d+)?)(?:\s*(.*))?$/;

const KITCHEN_FRACTIONS: Array<[number, string]> = [
  [1 / 8, "1/8"], [1 / 4, "1/4"], [1 / 3, "1/3"], [3 / 8, "3/8"],
  [1 / 2, "1/2"], [5 / 8, "5/8"], [2 / 3, "2/3"], [3 / 4, "3/4"],
  [7 / 8, "7/8"],
];

export function scaleQuantity(qty: string | null | undefined, factor: number): string | null | undefined {
  if (qty == null || typeof qty !== "string") return qty;
  if (!Number.isFinite(factor) || factor <= 0 || factor === 1) return qty;

  const trimmed = qty.trim();
  if (!trimmed) return qty;
  if (NON_NUMERIC_PREFIX.test(trimmed)) return qty;

  const m = trimmed.match(LEADING_NUMBER);
  if (!m) return qty;

  const numStr = m[1];
  const rest = (m[2] || "").trim();
  const num = parseQuantityNumber(numStr);
  if (num == null || num <= 0) return qty;

  const scaled = num * factor;
  const formatted = formatQuantityNumber(scaled);
  return rest ? `${formatted} ${rest}` : formatted;
}

export function parseQuantityNumber(s: string): number | null {
  s = s.trim();
  const mixed = s.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (mixed) {
    const w = parseInt(mixed[1], 10);
    const n = parseInt(mixed[2], 10);
    const d = parseInt(mixed[3], 10);
    if (d === 0) return null;
    return w + n / d;
  }
  const frac = s.match(/^(\d+)\/(\d+)$/);
  if (frac) {
    const n = parseInt(frac[1], 10);
    const d = parseInt(frac[2], 10);
    if (d === 0) return null;
    return n / d;
  }
  const num = parseFloat(s);
  return Number.isFinite(num) ? num : null;
}

export function formatQuantityNumber(n: number): string {
  const r = Math.round(n * 10000) / 10000;
  if (r === 0) return "0";
  if (Number.isInteger(r)) return String(r);

  const whole = Math.floor(r);
  const frac = r - whole;

  if (frac < 0.01) return String(whole);
  if (frac > 0.99) return String(whole + 1);

  let bestStr: string | null = null;
  let bestErr = 0.05;
  for (const [val, str] of KITCHEN_FRACTIONS) {
    const err = Math.abs(frac - val);
    if (err < bestErr) { bestErr = err; bestStr = str; }
  }
  if (bestStr) return whole > 0 ? `${whole} ${bestStr}` : bestStr;
  return String(r).replace(/\.?0+$/, "");
}
